import React, { useCallback, useEffect, useRef, useState } from 'react';
import { richTextIsEmpty, sanitizeRichTextHtml } from '../../utils/richText';
import './formsRichText.css';

const TOOLBAR_BUTTONS = [
  { cmd: 'bold', label: 'B', title: 'Bold', styleClass: 'forms-rich-editor-btn--bold' },
  { cmd: 'italic', label: 'I', title: 'Italic', styleClass: 'forms-rich-editor-btn--italic' },
  { cmd: 'underline', label: 'U', title: 'Underline', styleClass: 'forms-rich-editor-btn--underline' },
  { cmd: 'insertUnorderedList', label: '•', title: 'Bullet list' },
  { cmd: 'insertOrderedList', label: '1.', title: 'Numbered list' },
  { cmd: 'removeFormat', label: '⌫', title: 'Clear formatting' }
];

const TOGGLE_COMMANDS = new Set([
  'bold',
  'italic',
  'underline',
  'insertUnorderedList',
  'insertOrderedList'
]);

function readActiveCommands(editorEl) {
  const active = {};
  if (!editorEl || document.activeElement !== editorEl) return active;

  for (const cmd of TOGGLE_COMMANDS) {
    try {
      active[cmd] = document.queryCommandState(cmd);
    } catch {
      active[cmd] = false;
    }
  }
  return active;
}

function selectionInEditor(editorEl) {
  const sel = window.getSelection();
  if (!sel || sel.rangeCount === 0 || !editorEl) return false;
  const node = sel.anchorNode;
  return node ? editorEl.contains(node) : false;
}

const RichTextEditor = ({
  value,
  onChange,
  placeholder = '',
  className = '',
  id,
  'aria-labelledby': ariaLabelledBy
}) => {
  const editorRef = useRef(null);
  const [focused, setFocused] = useState(false);
  const [activeCommands, setActiveCommands] = useState({});
  const empty = richTextIsEmpty(value);

  const refreshActiveCommands = useCallback(() => {
    const el = editorRef.current;
    if (!el) return;
    if (selectionInEditor(el) || document.activeElement === el) {
      setActiveCommands(readActiveCommands(el));
    } else {
      setActiveCommands({});
    }
  }, []);

  useEffect(() => {
    const el = editorRef.current;
    if (!el || document.activeElement === el) return;
    const html = value || '';
    if (el.innerHTML !== html) {
      el.innerHTML = html;
    }
  }, [value]);

  useEffect(() => {
    const onSelectionChange = () => refreshActiveCommands();
    document.addEventListener('selectionchange', onSelectionChange);
    return () => document.removeEventListener('selectionchange', onSelectionChange);
  }, [refreshActiveCommands]);

  const emitChange = () => {
    const html = sanitizeRichTextHtml(editorRef.current?.innerHTML || '');
    onChange(html);
  };

  const runCommand = (cmd) => {
    editorRef.current?.focus();
    document.execCommand(cmd, false, null);
    emitChange();
    refreshActiveCommands();
  };

  return (
    <div className={`forms-rich-editor${className ? ` ${className}` : ''}`}>
      <div className="forms-rich-editor-toolbar" role="toolbar" aria-label="Formatting">
        {TOOLBAR_BUTTONS.map((btn) => {
          const isActive = Boolean(activeCommands[btn.cmd]);
          const isToggle = TOGGLE_COMMANDS.has(btn.cmd);
          return (
            <button
              key={btn.cmd}
              type="button"
              className={[
                'forms-rich-editor-btn',
                btn.styleClass,
                isActive ? 'forms-rich-editor-btn--active' : ''
              ]
                .filter(Boolean)
                .join(' ')}
              title={btn.title}
              aria-label={btn.title}
              aria-pressed={isToggle ? isActive : undefined}
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => runCommand(btn.cmd)}
            >
              {btn.label}
            </button>
          );
        })}
      </div>
      <div
        ref={editorRef}
        id={id}
        className={`forms-rich-editor-surface${focused ? ' forms-rich-editor-surface--focused' : ''}`}
        contentEditable
        role="textbox"
        aria-multiline="true"
        aria-labelledby={ariaLabelledBy}
        data-placeholder={placeholder}
        onInput={() => {
          emitChange();
          refreshActiveCommands();
        }}
        onFocus={() => {
          setFocused(true);
          refreshActiveCommands();
        }}
        onBlur={() => {
          setFocused(false);
          setActiveCommands({});
          emitChange();
        }}
        onKeyUp={refreshActiveCommands}
        onMouseUp={refreshActiveCommands}
        suppressContentEditableWarning
      />
      {empty && !focused ? (
        <div className="forms-rich-editor-placeholder" aria-hidden>
          {placeholder}
        </div>
      ) : null}
    </div>
  );
};

export default RichTextEditor;
